import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Home, Ticket, Settings, Camera, LogOut, Moon, Sun, User, ChevronDown, Menu, X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode, toggle } = useTheme();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); }, [location]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    setDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  const navLinkStyle = (path) => ({
    color: isActive(path) ? '#e94560' : '#ccc',
    textDecoration: 'none',
    padding: '7px 12px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    background: isActive(path) ? 'rgba(233,69,96,0.15)' : 'transparent',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: '0.2s'
  });

  const mobileLinkStyle = (path) => ({
    color: isActive(path) ? '#e94560' : darkMode ? '#ccc' : '#e0e0e0',
    textDecoration: 'none',
    padding: '14px 20px',
    fontSize: '15px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderBottom: `1px solid rgba(255,255,255,0.05)`,
    background: isActive(path) ? 'rgba(233,69,96,0.1)' : 'transparent',
    transition: '0.2s'
  });

  return (
    <>
      <nav style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        padding: '0 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '65px',
        boxShadow: '0 2px 20px rgba(0,0,0,0.3)',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        {/* Logo */}
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ background: 'linear-gradient(135deg, #e94560, #c62a47)', borderRadius: '10px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>🎓</div>
          <span style={{ color: '#fff', fontSize: '18px', fontWeight: '700' }}>
            College<span style={{ color: '#e94560' }}>Events</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} className="desktop-nav">
          <style>{`
            @media (max-width: 768px) { .desktop-nav { display: none !important; } .mobile-btn { display: flex !important; } }
            @media (min-width: 769px) { .mobile-btn { display: none !important; } }
          `}</style>

          <Link to="/" style={navLinkStyle('/')}><Home size={15} /> Home</Link>
          {user && <Link to="/my-tickets" style={navLinkStyle('/my-tickets')}><Ticket size={15} /> My Tickets</Link>}
          {user?.role === 'admin' && (
            <>
              <Link to="/admin" style={navLinkStyle('/admin')}><Settings size={15} /> Admin</Link>
              <Link to="/scanner" style={navLinkStyle('/scanner')}><Camera size={15} /> Scanner</Link>
            </>
          )}

          <button onClick={toggle} style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', padding: '7px 10px', borderRadius: '8px', display: 'flex', alignItems: 'center', marginLeft: '4px' }}>
            {darkMode ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          {user ? (
            <div ref={dropdownRef} style={{ position: 'relative', marginLeft: '8px' }}>
              <button onClick={() => setDropdownOpen(!dropdownOpen)} style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', padding: '7px 14px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid rgba(255,255,255,0.15)' }}>
                <div style={{ background: 'linear-gradient(135deg, #e94560, #c62a47)', borderRadius: '50%', width: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 'bold' }}>
                  {user.name?.charAt(0).toUpperCase()}
                </div>
                <span style={{ fontSize: '13px' }}>{user.name}</span>
                <ChevronDown size={13} style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0)', transition: '0.2s' }} />
              </button>

              {dropdownOpen && (
                <div style={{ position: 'absolute', top: '48px', right: 0, background: darkMode ? '#1a1a2e' : '#fff', borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.2)', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #eee', minWidth: '200px', overflow: 'hidden', zIndex: 2000 }}>
                  <div style={{ padding: '15px', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #f0f0f0', background: darkMode ? 'rgba(233,69,96,0.1)' : 'rgba(233,69,96,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ background: 'linear-gradient(135deg, #e94560, #c62a47)', borderRadius: '50%', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 'bold', color: '#fff' }}>
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p style={{ fontWeight: '600', fontSize: '14px', color: darkMode ? '#fff' : '#1a1a2e' }}>{user.name}</p>
                        <p style={{ fontSize: '12px', color: '#888' }}>{user.email}</p>
                        {user.rollNo && <p style={{ fontSize: '11px', color: '#e94560', fontWeight: '600' }}>Roll: {user.rollNo}</p>}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => { toggle(); setDropdownOpen(false); }} style={{ width: '100%', padding: '12px 15px', background: 'transparent', color: darkMode ? '#ccc' : '#555', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', borderRadius: '0', textAlign: 'left', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f5f5f5' }}>
                    {darkMode ? <Sun size={15} /> : <Moon size={15} />}
                    {darkMode ? 'Light Mode' : 'Dark Mode'}
                  </button>
                  <button onClick={handleLogout} style={{ width: '100%', padding: '12px 15px', background: 'transparent', color: '#e94560', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', borderRadius: '0', textAlign: 'left' }}>
                    <LogOut size={15} /> Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '8px', marginLeft: '10px' }}>
              <Link to="/login" style={{ color: '#ccc', textDecoration: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', border: '1px solid rgba(255,255,255,0.2)' }}>Login</Link>
              <Link to="/signup" style={{ background: 'linear-gradient(135deg, #e94560, #c62a47)', color: '#fff', textDecoration: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600' }}>Sign Up</Link>
            </div>
          )}
        </div>

        {/* Mobile Right Side */}
        <div className="mobile-btn" style={{ alignItems: 'center', gap: '8px' }}>
          <button onClick={toggle} style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', padding: '7px 10px', borderRadius: '8px', display: 'flex', alignItems: 'center' }}>
            {darkMode ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', padding: '7px 10px', borderRadius: '8px', display: 'flex', alignItems: 'center' }}>
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div style={{ background: '#1a1a2e', position: 'fixed', top: '65px', left: 0, right: 0, zIndex: 999, boxShadow: '0 8px 30px rgba(0,0,0,0.4)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          {user && (
            <div style={{ padding: '15px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ background: 'linear-gradient(135deg, #e94560, #c62a47)', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 'bold', color: '#fff', flexShrink: 0 }}>
                {user.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <p style={{ color: '#fff', fontWeight: '600', fontSize: '14px' }}>{user.name}</p>
                <p style={{ color: '#888', fontSize: '12px' }}>{user.email}</p>
                {user.rollNo && <p style={{ color: '#e94560', fontSize: '11px', fontWeight: '600' }}>Roll: {user.rollNo}</p>}
              </div>
            </div>
          )}

          <Link to="/" style={mobileLinkStyle('/')}><Home size={17} /> Home</Link>
          {user && <Link to="/my-tickets" style={mobileLinkStyle('/my-tickets')}><Ticket size={17} /> My Tickets</Link>}
          {user?.role === 'admin' && (
            <>
              <Link to="/admin" style={mobileLinkStyle('/admin')}><Settings size={17} /> Admin Dashboard</Link>
              <Link to="/scanner" style={mobileLinkStyle('/scanner')}><Camera size={17} /> QR Scanner</Link>
            </>
          )}

          {user ? (
            <button onClick={handleLogout} style={{ width: '100%', padding: '14px 20px', background: 'transparent', color: '#e94560', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '15px', border: 'none', borderTop: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer' }}>
              <LogOut size={17} /> Logout
            </button>
          ) : (
            <div style={{ padding: '15px 20px', display: 'flex', gap: '10px' }}>
              <Link to="/login" onClick={() => setMobileMenuOpen(false)} style={{ flex: 1, textAlign: 'center', padding: '12px', borderRadius: '10px', color: '#fff', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.2)', fontSize: '14px' }}>Login</Link>
              <Link to="/signup" onClick={() => setMobileMenuOpen(false)} style={{ flex: 1, textAlign: 'center', padding: '12px', borderRadius: '10px', color: '#fff', textDecoration: 'none', background: 'linear-gradient(135deg, #e94560, #c62a47)', fontSize: '14px', fontWeight: '600' }}>Sign Up</Link>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default Navbar;